/**
 * useMarketplaceOffers — thin hook wrapping the offer engine with app state.
 *
 * - Calls into pure offerEngine functions (which enforce assertEligible,
 *   race-on-accept, single-accept-per-job, append-only audit).
 * - On ACCEPT: writes canonical `assignedSubcontractorId` on the job AND
 *   sets the legacy `assignedSubcontractor` display string for back-compat.
 *   Sets `dispatchStatus = "Assigned"` and appends an activity entry.
 * - On DECLINE / WITHDRAW / EXPIRE: makes NO assignment writes; the job was
 *   never bound to the offered contractor. Smart Dispatch recomputes
 *   recommendations passively on next render — no auto-offer is emitted
 *   (Assisted Mode, no automation drift).
 *
 * Pricing rule: this module never reads or writes Job.financials.contractValue
 * or Job.payoutAmount on transition. The payout snapshot lives on the offer
 * itself for display only.
 */

import { useCallback } from "react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { MarketplaceService } from "@/services/marketplace.service";
import { JobService } from "@/services/job.service";
import {
  acceptOffer as acceptOfferPure,
  changeOfferWindow as changeOfferWindowPure,
  createOffer as createOfferPure,
  declineOffer as declineOfferPure,
  expireOffer as expireOfferPure,
  extendOffer as extendOfferPure,
  withdrawOffer as withdrawOfferPure,
  type CreateOfferInput,
  type OfferEngineResult,
} from "@/data/contractor/offerEngine";
import { assignContractor } from "@/data/contractor/assignContractor";
import { closeInterestForOfferSent } from "@/data/contractor/contractorInterest";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import { OFFER_TOAST } from "@/data/contractor/contractorMessaging";

function dbErrorSummary(error: unknown): string {
  if (!error || typeof error !== "object") return "Unknown database error";
  const e = error as { code?: string; message?: string; details?: string; hint?: string };
  return [e.code, e.message, e.details ?? e.hint].filter(Boolean).join(" · ");
}

function shouldAdvanceToAssignedAndDispatched(currentStage: string | undefined): boolean {
  if (!currentStage) return true;
  return [
    "Sale Closed",
    "Review and Validation",
    "Review Complete",
    "Ready for Ordering",
    "Ordered",
    "Permit and Order Pending",
    "Ready for Scheduling",
    "Scheduled Tentative",
    "Scheduled Confirmed",
    "Ready for Dispatch",
    "Available for Contractor Assignment",
  ].includes(currentStage);
}

export function useMarketplaceOffers() {
  const {
    marketplaceOffers,
    setMarketplaceOffers,
    contractors,
    setJobs,
    appendSystemEvents,
    setContractorInterests,
  } = useAppData();

  const persistOffer = useCallback(async (offer: MarketplaceOffer) => {
    await MarketplaceService.upsertOffer(offer);
  }, []);

  const getChangedOffers = useCallback(
    (nextOffers: MarketplaceOffer[]) =>
      nextOffers.filter((next) => {
        const prev = marketplaceOffers.find((offer) => offer.id === next.id);
        if (!prev) return true;
        return (
          prev.status !== next.status ||
          prev.expiresAt !== next.expiresAt ||
          prev.respondedAt !== next.respondedAt ||
          prev.responseReason !== next.responseReason ||
          prev.responseNote !== next.responseNote ||
          prev.audit.length !== next.audit.length
        );
      }),
    [marketplaceOffers],
  );

  const create = useCallback(
    (input: CreateOfferInput): OfferEngineResult => {
      let result: OfferEngineResult = { ok: false, reason: "not_found" };
      setMarketplaceOffers((prev) => {
        result = createOfferPure(prev, input);
        return result.ok ? result.offers : prev;
      });
      if (result.ok) {
        const created = (result as Extract<OfferEngineResult, { ok: true }>).offer;
        void persistOffer(created).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist created offer:", error);
          toast.error("Offer persistence failed", {
            description: dbErrorSummary(error),
          });
        });
        appendSystemEvents((result as Extract<OfferEngineResult, { ok: true }>).events);
        // P3: Close any open contractor interest for this (job, contractor) —
        // dispatcher has now responded via the offer loop.
        setContractorInterests((prev) => {
          const r = closeInterestForOfferSent(
            prev,
            input.job.id,
            input.contractor.id,
            input.sentBy,
          );
          if (r.closedCount === 0) return prev;
          void Promise.all(
            prev
              .filter(
                (x) =>
                  x.jobId === input.job.id &&
                  x.contractorId === input.contractor.id &&
                  x.status === "open",
              )
              .map((x) => MarketplaceService.closeInterestAsOfferSent(x.id)),
          ).catch((error) => {
            console.warn("[useMarketplaceOffers] Failed to persist interest close:", error);
            toast.error("Interest update failed", {
              description: "Offer was sent but contractor interest did not persist as closed.",
            });
          });
          appendSystemEvents(r.events);
          return r.interests;
        });
      }
      return result;
    },
    [setMarketplaceOffers, appendSystemEvents, setContractorInterests, persistOffer],
  );

  const accept = useCallback(
    (offerId: string, actor: string): OfferEngineResult => {
      let result: OfferEngineResult = { ok: false, reason: "not_found" };
      setMarketplaceOffers((prev) => {
        result = acceptOfferPure(prev, offerId, actor);
        return result.ok ? result.offers : prev;
      });
      // Side-effect: route canonical assignment write through assignContractor
      // boundary so contractor_unassigned + manual_assignment events are
      // emitted consistently. For offer_accept, the offerEngine already
      // emits offer_accepted; assignContractor adds contractor_unassigned
      // ONLY if the job had a different prior contractor.
      if (result.ok) {
        const accepted = (result as Extract<OfferEngineResult, { ok: true }>).offer;
        const nextOffers = (result as Extract<OfferEngineResult, { ok: true }>).offers;
        const changed = nextOffers.filter((next) => {
          const prev = marketplaceOffers.find((o) => o.id === next.id);
          if (!prev) return true;
          return (
            prev.status !== next.status ||
            prev.respondedAt !== next.respondedAt ||
            prev.responseReason !== next.responseReason ||
            prev.responseNote !== next.responseNote ||
            prev.audit.length !== next.audit.length
          );
        });
        void Promise.all(changed.map((o) => persistOffer(o))).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist accepted/superseded offers:", error);
          toast.error("Offer update failed", {
            description: "Offer status changed locally but failed to save to database.",
          });
        });
        const contractor = contractors.find((c) => c.id === accepted.contractorId);
        const today = new Date().toISOString().split("T")[0];
        appendSystemEvents((result as Extract<OfferEngineResult, { ok: true }>).events);
        setJobs((prev) => {
          const assignment = assignContractor(prev, {
            jobId: accepted.jobId,
            contractorId: accepted.contractorId,
            source: "offer_accept",
            actor,
            companyName: contractor?.companyName,
            offerId: accepted.id,
          });
          appendSystemEvents(assignment.events);
          return assignment.jobs.map((j) =>
            j.id === accepted.jobId
              ? {
                  ...j,
                  dispatchStatus: "Assigned" as const,
                  contractorAssignmentStatus: "Accepted",
                  currentStage: shouldAdvanceToAssignedAndDispatched(j.currentStage)
                    ? ("Assigned and Dispatched" as const)
                    : j.currentStage,
                  activityHistory: [
                    ...j.activityHistory,
                    {
                      id: `act-${Date.now()}`,
                      date: today,
                      action: `Marketplace offer ${accepted.id} accepted by ${contractor?.companyName ?? accepted.contractorId}`,
                      user: contractor?.companyName ?? accepted.contractorId,
                      noteType: "subcontractor" as const,
                    },
                  ],
                }
              : j,
          );
        });
        // Persist lifecycle + assignment mirrors to DB so refreshes and
        // downstream lifecycle projections stay consistent after acceptance.
        void JobService.update(accepted.jobId, {
          assignmentType: "Subcontractor",
          assignedSubcontractorId: accepted.contractorId,
          assignedSubcontractor: contractor?.companyName ?? undefined,
          contractorAssignmentStatus: "Accepted",
          dispatchStatus: "Assigned",
          currentStage: "Assigned and Dispatched",
        }).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist accepted job lifecycle:", error);
          toast.error("Acceptance sync failed", {
            description: "Offer was accepted, but job lifecycle update failed to persist.",
          });
        });
        // P4: action toast fires from the hook (single canonical location).
        toast.success(OFFER_TOAST.accepted.title, {
          description: OFFER_TOAST.accepted.description,
        });
      }
      return result;
    },
    [setMarketplaceOffers, setJobs, contractors, appendSystemEvents, marketplaceOffers, persistOffer],
  );

  const decline = useCallback(
    (
      offerId: string,
      actor: string,
      reason: import("@/data/contractor/marketplaceOffer").OfferDeclineReason,
      note?: string,
    ): OfferEngineResult => {
      let result: OfferEngineResult = { ok: false, reason: "not_found" };
      setMarketplaceOffers((prev) => {
        result = declineOfferPure(prev, offerId, actor, reason, note);
        return result.ok ? result.offers : prev;
      });
      if (result.ok) {
        const updated = (result as Extract<OfferEngineResult, { ok: true }>).offer;
        void persistOffer(updated).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist declined offer:", error);
          toast.error("Offer update failed", {
            description: "Decline was applied locally but failed to save to database.",
          });
        });
        appendSystemEvents((result as Extract<OfferEngineResult, { ok: true }>).events);
        // P4: action toast fires from the hook.
        toast(OFFER_TOAST.declined.title, {
          description: OFFER_TOAST.declined.description,
        });
      }
      // Passive auto-surface: no auto-offer cascade. Smart Dispatch
      // recomputes on next render because eligibility recommendations are
      // a pure derivation. Dispatcher must manually click "Offer" again.
      return result;
    },
    [setMarketplaceOffers, appendSystemEvents, persistOffer],
  );

  const withdraw = useCallback(
    (offerId: string, actor: string, reason?: string): OfferEngineResult => {
      let result: OfferEngineResult = { ok: false, reason: "not_found" };
      setMarketplaceOffers((prev) => {
        result = withdrawOfferPure(prev, offerId, actor, reason);
        return result.ok ? result.offers : prev;
      });
      if (result.ok) {
        const updated = (result as Extract<OfferEngineResult, { ok: true }>).offer;
        void persistOffer(updated).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist withdrawn offer:", error);
          toast.error("Offer update failed", {
            description: "Withdraw was applied locally but failed to save to database.",
          });
        });
        appendSystemEvents((result as Extract<OfferEngineResult, { ok: true }>).events);
      }
      return result;
    },
    [setMarketplaceOffers, appendSystemEvents, persistOffer],
  );

  const extend = useCallback(
    (
      offerId: string,
      ttlMinutes: number,
    ): { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } => {
      let result: { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } = {
        ok: false,
        reason: "not_found",
      };
      let engineResult: OfferEngineResult = { ok: false, reason: "not_found" };

      setMarketplaceOffers((prev) => {
        engineResult = extendOfferPure(prev, offerId, "dispatch", ttlMinutes);
        result = engineResult.ok
          ? { ok: true, offer: engineResult.offer }
          : { ok: false, reason: engineResult.reason === "not_found" ? "not_found" : "not_pending" };
        return engineResult.ok ? engineResult.offers : prev;
      });

      if (engineResult.ok) {
        const changed = getChangedOffers(engineResult.offers);
        void Promise.all(changed.map((offer) => persistOffer(offer))).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist extended offer:", error);
          toast.error("Offer update failed", {
            description: "Offer expiry changed locally but failed to save to database.",
          });
        });
        appendSystemEvents(engineResult.events);
      }

      return result;
    },
    [setMarketplaceOffers, persistOffer, appendSystemEvents, getChangedOffers],
  );

  const changeWindow = useCallback(
    (
      offerId: string,
      ttlMinutes: number,
    ): { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } => {
      let result: { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } = {
        ok: false,
        reason: "not_found",
      };
      let engineResult: OfferEngineResult = { ok: false, reason: "not_found" };

      setMarketplaceOffers((prev) => {
        engineResult = changeOfferWindowPure(prev, offerId, "dispatch", ttlMinutes);
        result = engineResult.ok
          ? { ok: true, offer: engineResult.offer }
          : { ok: false, reason: engineResult.reason === "not_found" ? "not_found" : "not_pending" };
        return engineResult.ok ? engineResult.offers : prev;
      });

      if (engineResult.ok) {
        const changed = getChangedOffers(engineResult.offers);
        void Promise.all(changed.map((offer) => persistOffer(offer))).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist changed offer window:", error);
          toast.error("Offer update failed", {
            description: "Offer window changed locally but failed to save to database.",
          });
        });
        appendSystemEvents(engineResult.events);
      }

      return result;
    },
    [setMarketplaceOffers, persistOffer, appendSystemEvents, getChangedOffers],
  );

  const expire = useCallback(
    (
      offerId: string,
    ): { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } => {
      let result: { ok: true; offer: MarketplaceOffer } | { ok: false; reason: "not_found" | "not_pending" } = {
        ok: false,
        reason: "not_found",
      };
      let engineResult: OfferEngineResult = { ok: false, reason: "not_found" };

      setMarketplaceOffers((prev) => {
        engineResult = expireOfferPure(prev, offerId, "dispatch");
        result = engineResult.ok
          ? { ok: true, offer: engineResult.offer }
          : { ok: false, reason: engineResult.reason === "not_found" ? "not_found" : "not_pending" };
        return engineResult.ok ? engineResult.offers : prev;
      });

      if (engineResult.ok) {
        const changed = getChangedOffers(engineResult.offers);
        void Promise.all(changed.map((offer) => persistOffer(offer))).catch((error) => {
          console.warn("[useMarketplaceOffers] Failed to persist expired offer:", error);
          toast.error("Offer update failed", {
            description: "Offer was marked expired locally but failed to save to database.",
          });
        });
        appendSystemEvents(engineResult.events);
      }

      return result;
    },
    [setMarketplaceOffers, persistOffer, appendSystemEvents, getChangedOffers],
  );

  return { offers: marketplaceOffers, create, accept, decline, withdraw, extend, changeWindow, expire };
}

export function getJobOffersFromList(
  jobId: string,
  offers: MarketplaceOffer[],
): MarketplaceOffer[] {
  return offers.filter((o) => o.jobId === jobId);
}
